import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MapPin,
  TrendingUp,
  RefreshCw,
  Calendar,
  ClipboardCheck,
  Users,
  ChevronRight,
} from 'lucide-react';
import { useFM } from '@/context/FMContext';
import { useTimeAdaptive } from '@/hooks/useTimeAdaptive';
import { getTimeSlotLabel, getGreeting } from '@/lib/fmUtils';
import { axiosInstance } from '@/App';

const QUICK_ACTIONS = [
  {
    label: 'Check-in',
    icon: MapPin,
    route: '/fm/checkin',
    color: 'blue',
    bgClass: 'bg-blue-50',
    iconClass: 'text-blue-500',
    borderClass: 'hover:border-blue-300 active:border-blue-400',
  },
  {
    label: 'PT Pipeline',
    icon: TrendingUp,
    route: '/fm/pipeline',
    color: 'green',
    bgClass: 'bg-green-50',
    iconClass: 'text-green-500',
    borderClass: 'hover:border-green-300 active:border-green-400',
  },
  {
    label: 'Renewals',
    icon: RefreshCw,
    route: '/fm/pipeline?type=renewal',
    color: 'purple',
    bgClass: 'bg-purple-50',
    iconClass: 'text-purple-500',
    borderClass: 'hover:border-purple-300 active:border-purple-400',
  },
  {
    label: 'Trials Today',
    icon: Calendar,
    route: '/fm/pipeline?stage=trial_scheduled',
    color: 'orange',
    bgClass: 'bg-orange-50',
    iconClass: 'text-orange-500',
    borderClass: 'hover:border-orange-300 active:border-orange-400',
  },
  {
    label: 'Compliance',
    icon: ClipboardCheck,
    route: '/fm/compliance',
    color: 'teal',
    bgClass: 'bg-teal-50',
    iconClass: 'text-teal-500',
    borderClass: 'hover:border-teal-300 active:border-teal-400',
  },
  {
    label: 'Trainers',
    icon: Users,
    route: '/fm/trainers',
    color: 'indigo',
    bgClass: 'bg-indigo-50',
    iconClass: 'text-indigo-500',
    borderClass: 'hover:border-indigo-300 active:border-indigo-400',
  },
];

/**
 * FMHome - Quick Actions grid (2x3) with time-adaptive greeting and stats.
 * The entry point for FM users, designed for one-tap access to any feature.
 */
export default function FMHome() {
  const navigate = useNavigate();
  const { selectedGym, selectedGymId } = useFM();
  const timeSlot = useTimeAdaptive();
  const [stats, setStats] = useState({ tasks: 0, pipeline: 0, escalations: 0 });
  const [statsLoading, setStatsLoading] = useState(true);

  // Fetch quick stats for the selected gym
  useEffect(() => {
    if (!selectedGymId) return;

    const fetchStats = async () => {
      setStatsLoading(true);
      try {
        const [sopRes, pipelineRes, escalationRes] = await Promise.allSettled([
          axiosInstance.get('/fm/sop', { params: { gym_id: selectedGymId, status: 'pending' } }),
          axiosInstance.get('/fm/pipeline', { params: { gym_id: selectedGymId } }),
          axiosInstance.get('/fm/escalations', { params: { gym_id: selectedGymId, status: 'open' } }),
        ]);

        setStats({
          tasks: sopRes.status === 'fulfilled'
            ? (sopRes.value.data.summary?.pending || sopRes.value.data.summary?.total || 0)
            : 0,
          pipeline: pipelineRes.status === 'fulfilled'
            ? (pipelineRes.value.data.total || pipelineRes.value.data.entries?.length || 0)
            : 0,
          escalations: escalationRes.status === 'fulfilled'
            ? (escalationRes.value.data.total || escalationRes.value.data.escalations?.length || 0)
            : 0,
        });
      } catch {
        // Stats are at-a-glance, not critical
      } finally {
        setStatsLoading(false);
      }
    };

    fetchStats();
  }, [selectedGymId]);

  const greeting = getGreeting();
  const timeLabel = getTimeSlotLabel(timeSlot);

  return (
    <div className="space-y-6">
      {/* Greeting + Time Slot */}
      <div>
        <h2 className="text-xl font-bold text-slate-800">{greeting}</h2>
        {selectedGym && (
          <p className="text-sm text-slate-500 mt-0.5">
            {selectedGym.name || selectedGym.gym_name || 'Your Gym'}
          </p>
        )}
        <span className="inline-block mt-2 text-xs font-medium px-2.5 py-1 rounded-full bg-slate-100 text-slate-600">
          {timeLabel}
        </span>
      </div>

      {/* Quick Actions Grid - 2x3 */}
      <div className="grid grid-cols-2 gap-4">
        {QUICK_ACTIONS.map((action) => {
          const Icon = action.icon;
          return (
            <button
              key={action.label}
              onClick={() => navigate(action.route)}
              className={`min-h-[120px] rounded-xl p-4 border border-transparent shadow-sm flex flex-col items-center justify-center gap-3 transition-all active:scale-95 ${action.bgClass} ${action.borderClass}`}
            >
              <Icon className={`w-8 h-8 ${action.iconClass}`} strokeWidth={1.75} />
              <span className="text-sm font-semibold text-slate-700">{action.label}</span>
            </button>
          );
        })}
      </div>

      {/* View All Gyms link */}
      <button
        onClick={() => navigate('/fm/portfolio')}
        className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-white border shadow-sm hover:bg-slate-50 active:scale-[0.98] transition-all"
      >
        <span className="text-sm font-medium text-slate-700">View All Gyms</span>
        <ChevronRight className="w-4 h-4 text-slate-400" />
      </button>

      {/* Quick Stats Row */}
      <div className="flex items-center justify-center gap-2 text-xs text-slate-500">
        {statsLoading ? (
          <div className="h-4 w-48 bg-slate-100 rounded animate-pulse" />
        ) : (
          <>
            <span>{stats.tasks} tasks pending</span>
            <span className="text-slate-300">|</span>
            <span>{stats.pipeline} pipeline leads</span>
            <span className="text-slate-300">|</span>
            <span>{stats.escalations} escalations</span>
          </>
        )}
      </div>
    </div>
  );
}
